import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Clock, Activity, CheckCircle, Search, Filter, TrendingUp, TrendingDown } from 'lucide-react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ENV } from '@/config/env';

type TabType = 'all' | 'quotes' | 'active' | 'completed';

export function ProviderMyJobsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'amount'>('newest');
  
  const [quotesSent, setQuotesSent] = useState<any[]>([]);
  const [activeJobs, setActiveJobs] = useState<any[]>([]);
  const [completedJobs, setCompletedJobs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAllJobs();
  }, []);

  const fetchAllJobs = async () => {
    setIsLoading(true);
    await Promise.all([
      fetchQuotesSent(),
      fetchActiveJobs(),
      fetchCompletedJobs()
    ]);
    setIsLoading(false);
  };

  const fetchQuotesSent = async () => {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quotes/sent`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setQuotesSent(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
    }
  };

  const fetchActiveJobs = async () => {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/jobs/active`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setActiveJobs(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch active jobs:', err);
    }
  };

  const fetchCompletedJobs = async () => {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/jobs/completed`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCompletedJobs(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch completed jobs:', err);
    }
  };

  const getAllJobs = () => {
    return [
      ...quotesSent.map(q => ({ ...q, type: 'quote', status: q.status || 'pending' })),
      ...activeJobs.map(j => ({ ...j, type: 'active', status: 'in_progress' })),
      ...completedJobs.map(j => ({ ...j, type: 'completed', status: 'completed' }))
    ];
  };

  const getFilteredJobs = () => {
    let jobs = getAllJobs();

    // Filter by tab
    if (activeTab === 'quotes') jobs = jobs.filter(j => j.type === 'quote');
    if (activeTab === 'active') jobs = jobs.filter(j => j.type === 'active');
    if (activeTab === 'completed') jobs = jobs.filter(j => j.type === 'completed');

    // Search filter
    if (searchTerm) {
      jobs = jobs.filter(j =>
        j.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        j.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    jobs.sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.created_at || b.updated_at || 0).getTime() - 
               new Date(a.created_at || a.updated_at || 0).getTime();
      }
      if (sortBy === 'oldest') {
        return new Date(a.created_at || a.updated_at || 0).getTime() - 
               new Date(b.created_at || b.updated_at || 0).getTime();
      }
      if (sortBy === 'amount') {
        return (b.amount || 0) - (a.amount || 0);
      }
      return 0;
    });

    return jobs;
  };

  const filteredJobs = getFilteredJobs();

  const tabs = [
    { id: 'all' as TabType, label: 'All', count: getAllJobs().length },
    { id: 'quotes' as TabType, label: 'Quotes Sent', count: quotesSent.length },
    { id: 'active' as TabType, label: 'In Progress', count: activeJobs.length },
    { id: 'completed' as TabType, label: 'Completed', count: completedJobs.length }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={[
          { label: 'Dashboard', href: '/provider/dashboard' },
          { label: 'My Jobs' }
        ]} />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 mt-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Jobs</h1>
            <p className="text-gray-600 mt-1">Manage all your quotes and jobs</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-x-auto">
          <div className="flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 min-w-[120px] px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600 bg-primary-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.label}
                <Badge variant="secondary" size="sm" className="ml-2">
                  {tab.count}
                </Badge>
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="p-4 flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="amount">Highest Amount</option>
            </select>
          </div>
        </Card>

        {/* Jobs List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <Card className="text-center py-12">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No jobs found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'Try adjusting your search' : 'Start browsing available jobs'}
            </p>
            <Button onClick={() => navigate('/provider-jobs')}>
              Browse Available Jobs
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <Card
                key={job.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/jobs/${job.id}`)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {job.title}
                        </h3>
                        {job.type === 'quote' && (
                          <Badge variant="warning" size="sm">
                            <Clock className="w-3 h-3 mr-1" />
                            Quote Sent
                          </Badge>
                        )}
                        {job.type === 'active' && (
                          <Badge variant="success" size="sm">
                            <Activity className="w-3 h-3 mr-1" />
                            In Progress
                          </Badge>
                        )}
                        {job.type === 'completed' && (
                          <Badge variant="secondary" size="sm">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {job.description}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-lg font-bold text-gray-900">
                        €{job.amount?.toLocaleString() || '—'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(job.created_at || job.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  
                  {job.location && (
                    <div className="text-sm text-gray-600">
                      📍 {job.location}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
